import { fetchRequestSend } from "../../helpers/common-helpers";

/** The editor's mount point. One per page, in whatever surface the host mounts it on. */
const EDITABLE_CLASS = 'editable-course-description';

/**
 * CustomCourseDescription class for managing course description editing.
 * Supports WYSIWYG and HTML modes with Froala and CodeMirror.
 */
export class CustomCourseDescription {
    /**
     * Initializes the CustomCourseDescription class.
     */
    constructor() {
        /** @type {FroalaEditor|null} froalaInstance - Instance of the Froala editor. */
        this.froalaInstance = null;

        /** @type {CodeMirror.Editor|null} codeMirrorInstance - Instance of the CodeMirror editor. */
        this.codeMirrorInstance = null;

        /** @type {boolean} isHtmlMode - Whether the editor is in HTML mode. */
        this.isHtmlMode = false;

        /** @type {NodeJS.Timeout|null} saveTimeout - Timeout ID for save operation. */
        this.saveTimeout = null;

        this.init();
    }

    /**
     * Initializes the editor and sets up event listeners.
     */
    init() {
        this.setupEventListeners();
        this.initializeFroala();
    }

    /**
     * Sets up event listeners for switching editor modes and handling input changes.
     */
    setupEventListeners() {
        // ⛔ NOT scoped to `.sidebar` any more. The editor is the SAME one wherever the host puts it,
        // and the learning plan's moved into the Settings slide-over — which is a `.panel`, not a
        // sidebar. Every host still renders exactly one `.editable-course-description`, so the
        // narrower selector was describing where the markup happened to sit, not what it is.
        const htmlButton = document.querySelector(`.${EDITABLE_CLASS}_btn.is-html`);
        const wysiwygButton = document.querySelector(`.${EDITABLE_CLASS}_btn.is-wysiwyg`);

        // Ensure buttons exist before adding event listeners.
        if (!htmlButton || !wysiwygButton) return;

        htmlButton.addEventListener('click', () => this.switchToHtmlMode(htmlButton, wysiwygButton));
        wysiwygButton.addEventListener('click', () => this.switchToWysiwygMode(htmlButton, wysiwygButton));
    }

    /**
     * Initializes the Froala editor instance.
     */
    initializeFroala() {
        const keySFR = "lSA3D-17C2D1B1E1D1B1C2sGXd1WWTDSGXYOUKh1KINLb1OG1g1H-8D1B3D3D1E6D1B1B4E4F3==";

        if (this.froalaInstance) return;
        // if Forala is not loaded - return
        if (typeof FroalaEditor === 'undefined') return;
        this.froalaInstance = new FroalaEditor(`.${EDITABLE_CLASS}`, {
            key: keySFR,
            attribution: false,
            toolbarInline: true,
            charCounterCount: false,
            linkAlwaysBlank: true,
            htmlUntouched: true, // Disables all sanitization
            htmlAllowedTags: ['i', 'ul', 'li', 'a', 'p', 'img', 'div', 'span'],
            htmlAllowedAttrs: ['class', 'href', 'target', 'src', 'alt', 'style'],
            toolbarButtons: [
                ['paragraphFormat', 'bold', 'italic', 'underline', 'strikeThrough', '|', 'clearFormatting'],
                '-',
                ['align', 'formatOL', 'formatUL']
            ],
            pluginsEnabled: [
                'align', 'link', 'lists', 'paragraphFormat'
            ],
            events: {
                'contentChanged': () => this.scheduleSaveDescription(),
                'paste.after': () => this.scheduleSaveDescription(),
            }
        });
    }

    /**
     * Destroys the Froala editor instance.
     */
    destroyFroala() {
        if (this.froalaInstance) {
            this.froalaInstance.destroy();
            this.froalaInstance = null;
        }
    }

    /**
     * Initializes the CodeMirror editor instance.
     * @param {string} htmlContent - The HTML content to populate the editor.
     */
    initializeCodeMirror(htmlContent) {
        const editableContainer = document.querySelector(`.${EDITABLE_CLASS}`);
        if (!editableContainer) return;

        const textarea = document.createElement('textarea');
        textarea.classList.add('editable-html-view');
        textarea.value = htmlContent.trim();

        editableContainer.innerHTML = '';
        editableContainer.appendChild(textarea);

        this.codeMirrorInstance = CodeMirror.fromTextArea(textarea, {
            mode: 'htmlmixed',
            lineWrapping: true,
            tabSize: 4
        });
    }

    /**
     * Destroys the CodeMirror editor instance and retrieves its content.
     * @returns {string} The HTML content from CodeMirror.
     */
    destroyCodeMirror() {
        if (!this.codeMirrorInstance) return '';

        const htmlContent = this.codeMirrorInstance.getValue();
        this.codeMirrorInstance.toTextArea();
        this.codeMirrorInstance = null;
        return htmlContent;
    }

    /**
     * Switches the editor to HTML mode.
     * @param {HTMLElement} htmlButton - The HTML mode button.
     * @param {HTMLElement} wysiwygButton - The WYSIWYG mode button.
     */
    switchToHtmlMode(htmlButton, wysiwygButton) {
        if (this.isHtmlMode) return;
        this.isHtmlMode = true;

        this.destroyFroala();

        const editableContainer = document.querySelector(`.${EDITABLE_CLASS}`);
        const currentContent = editableContainer ? editableContainer.innerHTML.trim() : '';
        this.initializeCodeMirror(currentContent);

        htmlButton.classList.add('is-active');
        wysiwygButton.classList.remove('is-active');
    }

    /**
     * Switches the editor to WYSIWYG mode.
     * @param {HTMLElement} htmlButton - The HTML mode button.
     * @param {HTMLElement} wysiwygButton - The WYSIWYG mode button.
     */
    switchToWysiwygMode(htmlButton, wysiwygButton) {
        if (!this.isHtmlMode) return;
        this.isHtmlMode = false;

        const updatedHtml = this.destroyCodeMirror();

        const editableContainer = document.querySelector(`.${EDITABLE_CLASS}`);
        if (editableContainer) {
            editableContainer.innerHTML = updatedHtml;
        }

        this.initializeFroala();

        wysiwygButton.classList.add('is-active');
        htmlButton.classList.remove('is-active');

        this.scheduleSaveDescription();
    }

    /**
     * Schedules saving the course description to prevent frequent saves.
     */
    scheduleSaveDescription() {
        clearTimeout(this.saveTimeout);
        this.saveTimeout = setTimeout(() => this.saveDescription(), 1000);
    }

    /**
     * Saves the course description by sending it to the server.
     */
    saveDescription() {
        const editableContainer = document.querySelector(`.${EDITABLE_CLASS}`);
        if (!editableContainer) return;

        let content = this.isHtmlMode
            ? this.codeMirrorInstance.getValue()
            : this.froalaInstance.html.get();

        const body = {
            description: content,
            course_id: courseID // courseID must be defined globally or passed to the class.
        };

        fetchRequestSend(saveCourseDescriptionURL, 'POST', body).then(response => {
            console.log('Description saved:', response);
        });
    }
}
